import { useState } from 'react';

import dayjs from 'dayjs';
import { MatchCandidateResponse } from 'generated/index';
import { type Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { useDebounceValue } from 'usehooks-ts';

import { planUnitsAtom } from 'components/roster-portal/activity-planning/atoms';
import type { Event } from 'components/roster-portal/activity-planning/types';

import { queries } from 'RestAPIs/rapini';

import { getPlanUnitIds } from 'components/utils';
import {
  Autocomplete,
  AutocompleteChangeReason,
  TextField
} from '@mui/material';

import {
  StyledEmployeeNameTypography,
  StyledOptionBox,
  StyledOptionRow,
  StyledSecondaryTypography
} from './EmploymentSelect.styles';

type Props = {
  autoFocus?: boolean;
  control: Control<any>;
  event?: Event;
  candidateEmployment?: MatchCandidateResponse | null;
  handleSelect?: (candidate: MatchCandidateResponse | undefined | null) => void;
};

const getOptionLabel = (option: MatchCandidateResponse) => {
  return option?.employeeName
    ? `${option.employeeName} (${option.employmentNumber})`
    : '';
};

export default function EmploymentSelect({
  autoFocus,
  control,
  event,
  candidateEmployment,
  handleSelect
}: Props) {
  const { t } = useTranslation();
  const selectedPlanUnits = useRecoilValue(planUnitsAtom);

  const [debouncedQuery, setDebouncedQuery] = useDebounceValue('', 500);
  const [isOpen, setIsOpen] = useState(false);

  const { data: options = [], isFetching } =
    queries.useFindMatchCandidatesForAppointments(
      event?.id ?? '',
      dayjs(event?.start)
        .startOf('day')
        .format('YYYY-MM-DD'),
      //@ts-expect-error TODO: BE should accept array of ids
      debouncedQuery.length > 0
        ? undefined
        : getPlanUnitIds(selectedPlanUnits).join(','),
      debouncedQuery.length > 0 ? debouncedQuery : undefined,
      true,
      { enabled: !!event?.id }
    );

  return (
    <Controller
      name="candidateEmployment"
      control={control}
      render={({ field }) => {
        // Keep Controller field in sync when pre-selection sets form value externally
        const currentValue = candidateEmployment ?? field.value ?? null;

        return (
          <Autocomplete<MatchCandidateResponse, false, false, false>
            open={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            openOnFocus={autoFocus}
            value={currentValue}
            options={options}
            loading={isFetching}
            loadingText={`${t('searching')}...`}
            noOptionsText={t('searchEmployee')}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, value) =>
              option.employmentEntityId === value?.employmentEntityId
            }
            onInputChange={(_, newInput, reason) => {
              if (reason === 'input') {
                setDebouncedQuery(newInput);
              }
            }}
            onChange={(
              _,
              value: MatchCandidateResponse | null,
              reason: AutocompleteChangeReason
            ) => {
              field.onChange(value);
              if (reason === 'clear') {
                handleSelect?.(null);
                setDebouncedQuery('');
              } else {
                handleSelect?.(value);
              }
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.employmentEntityId}>
                <StyledOptionBox>
                  <StyledOptionRow>
                    <StyledEmployeeNameTypography variant="body2" noWrap>
                      {option.employeeName}
                    </StyledEmployeeNameTypography>
                    <StyledSecondaryTypography
                      variant="caption"
                      color="text.secondary"
                    >
                      {option.employeeNumber}/{option.employmentNumber}
                    </StyledSecondaryTypography>
                  </StyledOptionRow>
                  <StyledOptionRow>
                    {option.function?.description && (
                      <StyledEmployeeNameTypography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                      >
                        {option.function.description}
                      </StyledEmployeeNameTypography>
                    )}
                    {option.organizationalUnit?.name && (
                      <StyledSecondaryTypography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                      >
                        {option.organizationalUnit.name}
                      </StyledSecondaryTypography>
                    )}
                  </StyledOptionRow>
                </StyledOptionBox>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                label={t('employee')}
                fullWidth
                autoFocus={autoFocus}
              />
            )}
          />
        );
      }}
    />
  );
}
